import { useState } from 'react';
import { adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Modal, ConfirmModal } from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';

const DEFAULT_FORM = { name: '', description: '', icon: '🧺', is_active: true };

export const ServiceCategories = () => {
  const toast = useToast();
  const { data: raw, loading, error, refetch, setData } = useApi(() => adminApi.getServiceCategories());

  const [createModal, setCreateModal] = useState(false);
  const [editModal,   setEditModal]   = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [form,        setForm]        = useState(DEFAULT_FORM);
  const [saving,      setSaving]      = useState(false);
  const [deleting,    setDeleting]    = useState(false);

  const categories = raw?.data || [];

  const openEdit = (cat) => {
    setEditModal(cat);
    setForm({ name: cat.name, description: cat.description || '', icon: cat.icon || '🧺', is_active: cat.is_active ?? true });
  };

  const handleSave = async (isEdit = false) => {
    if (!form.name?.trim()) { toast.error('Le nom est obligatoire.'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await adminApi.updateServiceCategory(editModal.category_id, form);
        setData(prev => ({
          ...prev,
          data: prev.data.map(c => c.category_id === editModal.category_id ? { ...c, ...form } : c),
        }));
        toast.success(`Catégorie "${form.name}" mise à jour ✓`);
        setEditModal(null);
      } else {
        const res = await adminApi.createServiceCategory(form);
        setData(prev => ({ ...prev, data: [...(prev.data || []), { ...form, category_id: res.data?.category_id }] }));
        toast.success(`Catégorie "${form.name}" créée ✓`);
        setCreateModal(false);
        setForm(DEFAULT_FORM);
      }
    } catch (err) {
      toast.error(err.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminApi.deleteServiceCategory(deleteModal.category_id);
      setData(prev => ({ ...prev, data: prev.data.filter(c => c.category_id !== deleteModal.category_id) }));
      toast.success(`Catégorie "${deleteModal.name}" supprimée.`);
      setDeleteModal(null);
    } catch (err) {
      toast.error(err.message || 'Erreur');
    } finally {
      setDeleting(false);
    }
  };

  const FormFields = () => (
    <>
      <div className="form-row">
        <div className="form-group" style={{ maxWidth: 80 }}>
          <label className="form-label">Icône</label>
          <input className="form-input" value={form.icon} style={{ fontSize: 22, textAlign: 'center' }}
            onChange={e => setForm(p => ({ ...p, icon: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Nom *</label>
          <input className="form-input" placeholder="Ex: Vêtements, Linge de maison..." value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea className="form-textarea" rows={2} placeholder="Description optionnelle..." value={form.description}
          onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13 }}>
        <input type="checkbox" checked={!!form.is_active} style={{ width: 16, height: 16, accentColor: 'var(--green)' }}
          onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} />
        <span>Catégorie active (visible dans l'app)</span>
      </label>
    </>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 24, marginBottom: 6 }}>🗂️ Catégories de Services</div>
        <div style={{ color: 'var(--text2)', fontSize: 13 }}>Gérez les catégories de services proposées par les laveries (Vêtements, Linge de maison, Spécial, etc.)</div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => { setForm(DEFAULT_FORM); setCreateModal(true); }}>
          + Nouvelle catégorie
        </button>
      </div>

      {loading ? <Loading text="Chargement..." />
        : error ? <ApiError message={error} onRetry={refetch} />
        : (
          <div className="grid-2" style={{ gap: 16 }}>
            {categories.length === 0 ? (
              <EmptyState icon="🗂️" title="Aucune catégorie" text="Créez la première catégorie de service."
                action={<button className="btn btn-primary" onClick={() => { setForm(DEFAULT_FORM); setCreateModal(true); }}>+ Nouvelle catégorie</button>} />
            ) : categories.map(cat => (
              <div key={cat.category_id} className="card" style={{
                display: 'flex', alignItems: 'center', gap: 16,
                opacity: cat.is_active === false ? 0.6 : 1,
              }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                  background: 'var(--bg3)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 26,
                }}>
                  {cat.icon || '🧺'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{cat.name}</div>
                  {cat.description && (
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>{cat.description}</div>
                  )}
                  <div style={{ display: 'flex', gap: 6 }}>
                    {cat.is_active === false
                      ? <span style={{ fontSize: 11, color: 'var(--orange)' }}>⏸ Inactive</span>
                      : <span style={{ fontSize: 11, color: 'var(--green)' }}>✓ Active</span>}
                    {cat.services_count != null && (
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>· {cat.services_count} service(s)</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(cat)}>✏️</button>
                  <button className="btn btn-danger btn-sm" onClick={() => setDeleteModal(cat)}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Créer */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title="Nouvelle catégorie" size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setCreateModal(false)}>Annuler</button>
          <button className="btn btn-primary" onClick={() => handleSave(false)} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '+ Créer'}
          </button>
        </>}>
        <FormFields />
      </Modal>

      {/* Éditer */}
      <Modal open={!!editModal} onClose={() => setEditModal(null)} title={`Modifier — ${editModal?.name}`} size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setEditModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={() => handleSave(true)} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '💾 Sauvegarder'}
          </button>
        </>}>
        <FormFields />
      </Modal>

      {/* Supprimer */}
      <ConfirmModal open={!!deleteModal} onClose={() => setDeleteModal(null)} onConfirm={handleDelete}
        title="Supprimer la catégorie" danger loading={deleting}
        message={deleteModal ? `Supprimer "${deleteModal.name}" ? Les services associés ne seront pas supprimés.` : ''} />
    </div>
  );
};
